import { DownloadOutlined, VideoCameraOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Flex, Space, Tag, Typography } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useI18n } from "@/i18n";
import { AGENT_ORIGIN, type AgentStatus } from "./agent";
import { uploadVideo, type Recorder, type Segment } from "./recorder";

type Pending = { orderId: string; externalId: string; seg: Segment; state: "uploading" | "failed" };

/**
 * Recordings waiting to reach the server, one at a time so the desk's
 * bandwidth goes to one parcel; failed ones stay for a retry.
 */
export function useVideoUploads() {
  const [items, setItems] = useState<Pending[]>([]);
  const send = useCallback(async (p: Pending) => {
    setItems((xs) => xs.map((x) => (x.orderId === p.orderId ? { ...x, state: "uploading" } : x)));
    try {
      await uploadVideo(p.orderId, p.seg);
      setItems((xs) => xs.filter((x) => x.orderId !== p.orderId));
    } catch {
      setItems((xs) => xs.map((x) => (x.orderId === p.orderId ? { ...x, state: "failed" } : x)));
    }
  }, []);
  const push = useCallback(
    (orderId: string, externalId: string, seg: Segment) => {
      const p: Pending = { orderId, externalId, seg, state: "uploading" };
      setItems((xs) => [...xs.filter((x) => x.orderId !== orderId), p]);
      void send(p);
    },
    [send],
  );
  return { items, push, retry: send };
}

/** The desk's camera: a small live preview, whether it records, and uploads. */
export function PackingCamera({ rec, uploads }: { rec: Recorder; uploads: ReturnType<typeof useVideoUploads> }) {
  const { t } = useI18n();
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.srcObject = rec.stream;
  }, [rec.stream]);
  const failed = uploads.items.filter((x) => x.state === "failed");
  const sending = uploads.items.length - failed.length;
  if (rec.error) return <Alert type="warning" showIcon style={{ marginBottom: 16 }} title={t("wms.video.noCamera")} description={t("wms.video.noCameraHelp")} />;
  return (
    <Card size="small" style={{ marginBottom: 16 }}>
      <Flex gap={12} align="center" wrap>
        <video ref={ref} muted autoPlay playsInline style={{ width: 160, height: 90, background: "black", borderRadius: 4, objectFit: "cover" }} />
        <Space orientation="vertical" size={2}>
          <Space>
            <VideoCameraOutlined />
            {rec.recording ? <Tag color="red">● REC</Tag> : <Tag>{t(rec.ready ? "wms.video.standby" : "wms.video.starting")}</Tag>}
          </Space>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t("wms.video.help")}
          </Typography.Text>
          {sending > 0 && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t("wms.video.uploading", { n: sending })}
            </Typography.Text>
          )}
        </Space>
        {failed.length > 0 && (
          <Space wrap>
            <Typography.Text type="danger">{t("wms.video.failed", { n: failed.length })}</Typography.Text>
            {failed.map((f) => (
              <Button key={f.orderId} size="small" onClick={() => void uploads.retry(f)}>
                {t("wms.video.retry", { number: f.externalId })}
              </Button>
            ))}
          </Space>
        )}
      </Flex>
    </Card>
  );
}

/** The desk records through the agent on this laptop: its state at a glance. */
export function AgentCamera({ rec }: { rec: Recorder & { status: AgentStatus | null } }) {
  const { t, tDynamic } = useI18n();
  const s = rec.status;
  if (rec.error) {
    return (
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        title={tDynamic(`wms.agent.${rec.error}`, "wms.agent.agent_camera")}
        description={rec.error === "agent_camera" && s?.cameraError ? s.cameraError : t("wms.agent.fixHelp")}
        action={
          <Space orientation="vertical">
            <Button size="small" href={AGENT_ORIGIN} target="_blank">
              {t("wms.agent.openSetup")}
            </Button>
            <Button size="small" icon={<DownloadOutlined />} href="/downloads/zerpid-agent.zip">
              {t("wms.agent.download")}
            </Button>
          </Space>
        }
      />
    );
  }
  return (
    <Card size="small" style={{ marginBottom: 16 }}>
      <Flex gap={12} align="center" wrap>
        <VideoCameraOutlined style={{ fontSize: 20 }} />
        {rec.recording ? <Tag color="red">● REC</Tag> : <Tag>{t(rec.ready ? "wms.video.standby" : "wms.video.starting")}</Tag>}
        <Typography.Text>{t("wms.agent.on", { name: s?.name ?? "", camera: s?.camera ?? "" })}</Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t("wms.agent.keptHere")}
        </Typography.Text>
        {/* The camera in a small window of its own, moved aside while packing (agent 0.3.1+). */}
        <Button size="small" onClick={() => window.open(AGENT_ORIGIN + "/preview", "zerpid-camera", "popup,width=520,height=380")}>
          {t("wms.agent.preview")}
        </Button>
      </Flex>
    </Card>
  );
}
